import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Input, Segmented, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faPaperclip, faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  dailyClassAdjustmentLabel,
  formatApprovalDeadline,
  describeInvoiceIssue,
  formatWithholdingPct,
  invoiceCheckBlocks,
  deductionLabel,
  oneTimeBonusLabel,
  volumeBonusLabel,
  conversionBonusLabel,
  withholdingOf,
  VAT_RATE,
  hugCoursePay,
  manualSpreadLabel,
  sumManualLines,
  type ApprovalView,
} from '@salaries/shared';
import {
  addApprovalInvoiceUrl,
  approveSalary,
  getApproval,
  requestCorrection,
  uploadApprovalInvoice,
} from '../api/approval';
import { formatMoney, Money } from '../components/Money';
import { Loader } from '../components/Loader';

function Line({
  label,
  value,
  strong,
  negative,
}: {
  label: string;
  value: number;
  strong?: boolean;
  negative?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${strong ? 'font-semibold' : ''}`}>
      <span className="text-main-text">{label}</span>
      <span className={negative ? 'text-error-red' : undefined}>
        {negative && '-'}
        <Money value={value} />
      </span>
    </div>
  );
}

// The verdict on the attached חשבונית's amount, shown right under the attach
// controls. Covers both ways in: an uploaded file, and a link (the server fetches
// the document behind it, or rasterizes the page). Renders nothing until a check
// has actually run — a box that doesn't verify amounts, or a coach who hasn't
// attached anything yet, sees no extra noise.
//
// The three outcomes read very differently on purpose:
//   match      — a quiet confirmation; nothing is required of the coach
//   mismatch   — the actionable one: one line per failed field (amount, עוסק
//                number, ח״פ, מספר הקצאה) saying exactly WHAT to fix.
//                Blocking or not depends on the box's setting.
//   unreadable — we say so plainly rather than implying the invoice is wrong,
//                and say that it doesn't stop the approval. A coach staring at a
//                blocked button with no explanation is the worst outcome here.
function InvoiceCheckNotice({ v }: { v: ApprovalView }) {
  const status = v.invoice_check_status;
  if (v.invoice_amount_check === 'none' || !status) return null;

  const expected = v.computation.total;

  if (status === 'match') {
    return (
      <div className="text-small text-success-green mt-2">
        ✓ החשבונית נבדקה ותקינה (סכום: {formatMoney(v.invoice_amount ?? expected)}).
      </div>
    );
  }

  if (status === 'unreadable') {
    // Wording depends on how the invoice got here: a link we couldn't open needs
    // different advice from a photo we couldn't read.
    const byLink = !v.invoice_uploaded && !!v.invoice_url;
    return (
      <Alert
        className="mt-3"
        type="info"
        showIcon
        message={byLink ? 'לא הצלחנו לפתוח את הקישור' : 'לא הצלחנו לקרוא את החשבונית מהקובץ'}
        description={
          byLink
            ? 'ניתן להמשיך ולאשר. אם הקישור דורש התחברות, כדאי להעלות את החשבונית כקובץ.'
            : 'ניתן להמשיך ולאשר. אם הקובץ מטושטש או חתוך, כדאי להעלות עותק ברור יותר.'
        }
      />
    );
  }

  const blocking = invoiceCheckBlocks(v.invoice_amount_check, status);
  return (
    <Alert
      className="mt-3"
      type={blocking ? 'error' : 'warning'}
      showIcon
      message="יש לתקן את החשבונית"
      description={
        <>
          <ul className="list-disc ps-5">
            {(v.invoice_check_issues ?? []).map((i) => (
              <li key={i.field}>{describeInvoiceIssue(i)}</li>
            ))}
          </ul>
          <div className="mt-1">
            {blocking
              ? 'יש לצרף חשבונית מתוקנת כדי לאשר. אם לדעתך הדוח שגוי, אפשר לבקש תיקון.'
              : 'אפשר לאשר בכל זאת, או לצרף חשבונית מתוקנת.'}
          </div>
        </>
      }
    />
  );
}

// Arbox-branded shell: a centered card on the grey background, full-width and
// comfortably padded on mobile (100dvh, no clipping), capped on desktop.
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-grey-bg px-4 py-6">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-popup p-5 sm:p-6">{children}</div>
        <div
          dir="ltr"
          className="flex items-center justify-center gap-2 text-secondary-text mt-4"
          style={{ fontSize: 13 }}
        >
          <span>powered by</span>
          <a href="https://arbox.co.il" target="_blank" rel="noopener noreferrer" aria-label="Arbox">
            <img src="/arbox-logo.png" alt="Arbox" style={{ height: 24, width: 'auto' }} />
          </a>
        </div>
      </div>
    </div>
  );
}

export function ApprovalPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const [invoiceName, setInvoiceName] = useState<string | null>(null);
  const [mode, setMode] = useState<'file' | 'url'>('file');
  const [url, setUrl] = useState('');
  const [showCorrection, setShowCorrection] = useState(false);
  const [comment, setComment] = useState('');

  // Coaches open this page standalone; the shared app tab title says "שכר", so
  // override it here to keep the approval page free of salary wording.
  useEffect(() => {
    document.title = 'אישור הדוח | Arbox';
  }, []);

  const q = useQuery({
    queryKey: ['approval', token],
    queryFn: () => getApproval(token),
    retry: false,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['approval', token] });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadApprovalInvoice(token, file),
    onSuccess: () => {
      toast.success('החשבונית צורפה');
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בצירוף החשבונית'),
  });

  const urlMut = useMutation({
    mutationFn: (u: string) => addApprovalInvoiceUrl(token, u),
    onSuccess: () => {
      toast.success('הקישור לחשבונית נשמר');
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בשמירת הקישור'),
  });

  const approveMut = useMutation({
    mutationFn: () => approveSalary(token),
    onSuccess: () => {
      toast.success('הדוח אושר, תודה!');
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה באישור'),
  });

  const correctionMut = useMutation({
    mutationFn: () => requestCorrection(token, comment.trim()),
    onSuccess: () => {
      toast.success('בקשת התיקון נשלחה');
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בשליחת הבקשה'),
  });

  // Invoice attach controls (file upload OR url) — reused by the main approval
  // flow and the post-approval "attach invoice" section.
  const invoiceControls = (v: ApprovalView) => (
    <>
      <Segmented
        block
        value={mode}
        onChange={(m) => setMode(m as 'file' | 'url')}
        options={[
          { label: 'העלאת קובץ', value: 'file' },
          { label: 'קישור', value: 'url' },
        ]}
        className="mb-3"
      />
      {mode === 'file' ? (
        <Upload
          accept=".pdf,.png,.jpg,.jpeg"
          maxCount={1}
          beforeUpload={(file) => {
            setInvoiceName(file.name);
            uploadMut.mutate(file);
            return false; // prevent antd's own upload
          }}
          fileList={
            invoiceName ? ([{ uid: '1', name: invoiceName, status: 'done' }] as UploadFile[]) : []
          }
        >
          <Button icon={<FontAwesomeIcon icon={faPaperclip} />} loading={uploadMut.isPending} block>
            {/* Reading the invoice adds a few seconds to the upload — say what
                we're doing rather than leaving a spinner with a stale label. */}
            {uploadMut.isPending && v.invoice_amount_check !== 'none'
              ? 'בודקים את הסכום בחשבונית…'
              : 'העלאת חשבונית (PDF, PNG או JPG)'}
          </Button>
        </Upload>
      ) : (
        <div className="flex gap-2">
          <Input
            dir="ltr"
            placeholder="https://…"
            value={url}
            inputMode="url"
            onChange={(e) => setUrl(e.target.value)}
            onPressEnter={() => url.trim() && urlMut.mutate(url.trim())}
          />
          <Button
            type="primary"
            ghost
            loading={urlMut.isPending}
            disabled={!url.trim()}
            onClick={() => urlMut.mutate(url.trim())}
          >
            {/* Fetching + reading the linked document takes a few seconds. */}
            {urlMut.isPending && v.invoice_amount_check !== 'none' ? 'בודקים…' : 'צירוף'}
          </Button>
        </div>
      )}
      <InvoiceCheckNotice v={v} />
    </>
  );

  if (q.isLoading) return <Loader fullScreen />;

  if (q.isError || !q.data) {
    return (
      <Shell>
        <div className="text-center">
          <div className="text-heading-2 text-main-text mb-2">הקישור אינו תקין</div>
          <div className="text-small text-secondary-text">
            ייתכן שהקישור פג תוקף או שהשכר כבר אושר.
          </div>
        </div>
      </Shell>
    );
  }

  const v = q.data;
  const c = v.computation;

  // Already approved → the approval link is spent, but the coach can still
  // (re)attach an invoice (e.g. after the operator cleared it) via the same link.
  if (v.expired || v.status === 'approved') {
    const provided = v.invoice_uploaded || !!v.invoice_url;
    return (
      <Shell>
        <div className="text-center">
          <FontAwesomeIcon icon={faCircleCheck} className="text-success-green" style={{ fontSize: 48 }} />
          <div className="text-heading-2 text-main-text mt-3 mb-1">הדוח אושר</div>
          <div className="text-small text-secondary-text">
            אישרת את השכר עבור {v.period_label}. תודה!
          </div>
        </div>
        {v.invoice_policy !== 'none' && !v.deadline_passed && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="text-small text-main-text mb-2">
              {provided ? 'חשבונית צורפה ✓ — ניתן להחליף במידת הצורך' : 'ניתן לצרף חשבונית'}
            </div>
            {invoiceControls(v)}
          </div>
        )}
      </Shell>
    );
  }

  // Corrections requested → link paused until the owner re-sends.
  if (v.correction_requested || v.status === 'needs_correction') {
    return (
      <Shell>
        <div className="text-center">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-primary" style={{ fontSize: 44 }} />
          <div className="text-heading-2 text-main-text mt-3 mb-1">בקשת התיקון נשלחה</div>
          <div className="text-small text-secondary-text">
            העברנו את הערתך. לאחר התיקון יישלח אליך שכר מעודכן לאישור.
          </div>
        </div>
      </Shell>
    );
  }

  // Deadline passed (and not yet approved) → the link is read-only; the owner
  // must re-send to reopen approval. Mirrors the server-side block.
  if (v.deadline_passed) {
    return (
      <Shell>
        <div className="text-center">
          <FontAwesomeIcon icon={faClockRotateLeft} className="text-secondary-text" style={{ fontSize: 44 }} />
          <div className="text-heading-2 text-main-text mt-3 mb-1">חלף המועד לאישור</div>
          <div className="text-small text-secondary-text">
            {v.approval_deadline_at
              ? `המועד האחרון לאישור הדוח (${formatApprovalDeadline(v.approval_deadline_at)}) חלף. `
              : 'המועד האחרון לאישור הדוח חלף. '}
            לקבלת קישור חדש יש לפנות לבעל/ת העסק.
          </div>
        </div>
      </Shell>
    );
  }

  const invoiceProvided = v.invoice_uploaded || !!v.invoice_url;
  const needInvoice = v.invoice_policy === 'required' && !invoiceProvided;
  // Mirrors the server's block in routes/approval.ts — only a confidently-read
  // mismatch under 'block' stops the approval; an unreadable invoice does not.
  const amountBlocked = invoiceCheckBlocks(v.invoice_amount_check, v.invoice_check_status);
  const manualSums = sumManualLines(v.manual_lines ?? []);
  const manual = { manual_additions: manualSums.additions, manual_offsets: manualSums.offsets };

  return (
    <Shell>
      <div className="text-center mb-4">
        <div className="text-heading-2 text-main-text">אישור הדוח</div>
        <div className="text-small text-secondary-text">
          {v.employee_name} · {v.period_label}
          {v.pnl_unit_name ? ` · ${v.pnl_unit_name}` : ''}
        </div>
        {v.approval_deadline_at && (
          <div className="text-small text-secondary-text mt-1">
            ניתן לאשר עד {formatApprovalDeadline(v.approval_deadline_at)}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-3">
        {/* addons_pay and the manual additions/offsets are subsets of hug_pay —
            split out, never added on top. */}
        {hugCoursePay({ hug_pay: v.hug_pay ?? 0, addons_pay: v.addons_pay ?? 0, ...manual }) > 0 && (
          <Line
            label="שכר חוגים"
            value={hugCoursePay({ hug_pay: v.hug_pay ?? 0, addons_pay: v.addons_pay ?? 0, ...manual })}
          />
        )}
        {(v.addons_pay ?? 0) !== 0 && <Line label="תשלום על נלווים" value={v.addons_pay!} />}
        {/* תוספות וקיזוזים — each row with the description the box typed and
            where it is in its spread. An offset shows like the deduction. */}
        {(v.manual_lines ?? []).map((l) => (
          <Line
            key={`${l.kind}-${l.id}`}
            label={`${l.description} (${manualSpreadLabel(l)})`}
            value={l.amount}
            negative={l.kind === 'offset'}
          />
        ))}
        {c.class_pay > 0 && <Line label="שכר שיעורים" value={c.class_pay} />}
        {c.weekend_bonus > 0 && <Line label="תוספת סוף שבוע" value={c.weekend_bonus} />}
        {c.class_bonus > 0 && <Line label="תוספת שיעור" value={c.class_bonus} />}
        {(c.volume_bonus ?? 0) > 0 && (
          <Line label={volumeBonusLabel(c.class_count)} value={c.volume_bonus} />
        )}
        {/* `!== 0`, not `> 0`: this can be a pure deduction — Money already
            renders the sign, so no `negative` prop is needed here. */}
        {(c.daily_class_adjustment ?? 0) !== 0 && (
          <Line label={dailyClassAdjustmentLabel()} value={c.daily_class_adjustment} />
        )}
        {c.conversion_bonus > 0 && (
          <Line
            label={conversionBonusLabel(c.conversion_count, c.conversion_bonus)}
            value={c.conversion_bonus}
          />
        )}
        {c.base_salary > 0 && <Line label="שכר בסיס" value={c.base_salary} />}
        {c.travel > 0 && <Line label="נסיעות" value={c.travel} />}
        {(c.empty_class_pay ?? 0) > 0 && (
          <Line label="שיעורים ריקים" value={c.empty_class_pay} />
        )}
        {(c.cancelled_class_pay ?? 0) > 0 && (
          <Line label="שיעורים שבוטלו" value={c.cancelled_class_pay} />
        )}
        {c.one_time_bonus > 0 && (
          <Line
            label={oneTimeBonusLabel(c.one_time_bonus_percent, c.one_time_bonus_note)}
            value={c.one_time_bonus}
          />
        )}
        {/* The one-time deduction, with the reason the box gave for it — the
            coach is being asked to approve less than they earned, so the page
            has to say how much less and why. `negative` renders the minus. */}
        {(c.deduction ?? 0) > 0 && (
          <Line
            label={deductionLabel(c.deduction_percent, c.deduction_note)}
            value={c.deduction}
            negative
          />
        )}
        {/* VAT (עוסק מורשה) — without it the lines above don't add up to the
            invoice total. Negative on a month that nets below zero. */}
        {c.vat !== 0 && <Line label={`מע"מ (${Math.round(VAT_RATE * 100)}%)`} value={c.vat} />}
        <div className="border-t border-border mt-2 pt-2">
          {/* ניכוי מס במקור: the coach still invoices the full amount, so show
              that first, then the deduction, then what actually gets transferred.
              Snapshots frozen before the feature read as no withholding. */}
          {withholdingOf(c).withholding > 0 ? (
            <>
              <Line label='סה"כ חשבונית' value={c.total} />
              <Line
                label={`ניכוי מס במקור (${formatWithholdingPct(withholdingOf(c).withholding_pct)})`}
                value={withholdingOf(c).withholding}
                negative
              />
              <Line label='סה"כ להעברה' value={withholdingOf(c).net_total} strong />
            </>
          ) : (
            <Line label='סה"כ לתשלום' value={c.total} strong />
          )}
        </div>
      </div>

      {/* חשבונית — only when the box collects them. */}
      {v.invoice_policy !== 'none' && (
        <div className="mt-5">
          <div className="text-small text-main-text mb-2">
            {invoiceProvided
              ? 'חשבונית צורפה ✓'
              : v.invoice_policy === 'required'
                ? 'נדרשת חשבונית לפני האישור'
                : 'ניתן לצרף חשבונית (לא חובה)'}
          </div>
          {invoiceControls(v)}
        </div>
      )}

      <Button
        type="primary"
        block
        size="large"
        className="mt-6"
        disabled={needInvoice || amountBlocked}
        loading={approveMut.isPending}
        onClick={() => approveMut.mutate()}
      >
        {needInvoice
          ? 'יש לצרף חשבונית לפני האישור'
          : amountBlocked
            ? 'יש לצרף חשבונית בסכום הנכון'
            : 'אישור הדוח'}
      </Button>

      {/* צריך תיקונים — reveals a comment box that pauses the link on submit. */}
      {!showCorrection ? (
        <button
          type="button"
          onClick={() => setShowCorrection(true)}
          className="w-full text-center text-small text-secondary-text hover:text-primary mt-3 py-1"
        >
          צריך תיקונים?
        </button>
      ) : (
        <div className="mt-4 border-t border-border pt-4">
          <div className="text-small text-main-text mb-2">מה צריך לתקן?</div>
          <Input.TextArea
            rows={3}
            value={comment}
            maxLength={2000}
            onChange={(e) => setComment(e.target.value)}
            placeholder="כתוב/כתבי כאן מה לא תקין בשכר…"
          />
          <div className="flex gap-2 mt-3">
            <Button onClick={() => setShowCorrection(false)} block>
              ביטול
            </Button>
            <Button
              type="primary"
              danger
              block
              disabled={!comment.trim()}
              loading={correctionMut.isPending}
              onClick={() => correctionMut.mutate()}
            >
              שליחת בקשת תיקון
            </Button>
          </div>
        </div>
      )}
    </Shell>
  );
}
